import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Eye, Download, Send } from "lucide-react";
import { useState } from "react";
import { OrderDetailsDialog } from "./OrderDetailsDialog";
import InvoicePopup from "@/components/orders/InvoicePopup";
import { useIsMobile, useIsExtraSmall } from "@/hooks/use-mobile";
import { useTheme } from "@/context/ThemeContext";
import { toast } from "sonner";

import { ExtendedInvoice } from "@/types/payment";
import { formatDate } from "@/utils/dateUtils";
import { useFile } from "@/hooks/useFile";
import { useMedia } from "@/hooks/useMedia";
import { InvoiceService } from "@/services/invoice/invoiceService";
import { useLanguage } from "@/context/LanguageContext";
import { ordersLabels } from "@/i18n";

export const InvoiceList = ({
  invoices,
  onViewInvoice,
}: {
  invoices: ExtendedInvoice[];
  onViewInvoice?: (invoice: ExtendedInvoice) => void;
}) => {
  const { language } = useLanguage();
  const o = ordersLabels(language);
  const ae = o.adminEconomy as Record<string, string>;

  const { downloadFile } = useFile();
  const { getDownloadUrlAsync } = useMedia();
  const [sortBy, setSortBy] = useState<string>("date");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [selectedInvoice, setSelectedInvoice] =
    useState<ExtendedInvoice | null>(null);
  const [isInvoicePopupOpen, setIsInvoicePopupOpen] = useState(false);
  const isMobile = useIsMobile();
  const isExtraSmall = useIsExtraSmall();

  invoices = invoices.sort((a, b) => {
    let aVal: unknown, bVal: unknown;

    if (sortBy === "date") {
      aVal = a.invoice.created_at
        ? new Date(a.invoice.created_at)
        : new Date(0);
      bVal = b.invoice.created_at
        ? new Date(b.invoice.created_at)
        : new Date(0);
    } else if (sortBy === "regNumber") {
      aVal = a.listing.regNumber;
      bVal = b.listing.regNumber;
    } else if (sortBy === "amount") {
      aVal = a.payment.amount;
      bVal = b.payment.amount;
    } else if (sortBy === "customer") {
      aVal = `${a.customer.firstName} ${a.customer.lastName}`;
      bVal = `${b.customer.firstName} ${b.customer.lastName}`;
    } else if (sortBy === "status") {
      aVal = a.payment.status;
      bVal = b.payment.status;
    } else {
      aVal = a.invoice.id;
      bVal = b.invoice.id;
    }

    if (aVal < bVal) return sortDir === "asc" ? -1 : 1;
    if (aVal > bVal) return sortDir === "asc" ? 1 : -1;
    return 0;
  });

  const handleSort = (col: string) => {
    if (sortBy === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortBy(col);
      setSortDir("asc");
    }
  };

  const sortArrow = (col: string) =>
    sortBy === col ? (sortDir === "asc" ? "▲" : "▼") : "";

  return (
    <>
      <div className="relative overflow-x-auto">
        <Table>
          <TableHeader className={`bg-primary/10`}>
            <TableRow>
              <TableHead
                className="w-[90px] cursor-pointer"
                onClick={() => handleSort("id")}
              >
                {ae.id}
                {sortArrow("id")}
              </TableHead>
              {!isExtraSmall && (
                <TableHead
                  className="cursor-pointer"
                  onClick={() => handleSort("customer")}
                >
                  {ae.customer} {sortArrow("customer")}
                </TableHead>
              )}
              <TableHead
                className="cursor-pointer"
                onClick={() => handleSort("amount")}
              >
                {ae.amount} {sortArrow("amount")}
              </TableHead>
              {!isMobile && (
                <TableHead
                  className="cursor-pointer"
                  onClick={() => handleSort("date")}
                >
                  {ae.date} {sortArrow("date")}
                </TableHead>
              )}
              {!isMobile && (
                <TableHead
                  className="cursor-pointer"
                  onClick={() => handleSort("dueDate")}
                >
                  {ae.dueDate} {sortArrow("dueDate")}
                </TableHead>
              )}
              <TableHead
                className="cursor-pointer"
                onClick={() => handleSort("regNumber")}
              >
                {ae.regNo} {sortArrow("regNumber")}
              </TableHead>
              <TableHead
                className="cursor-pointer"
                onClick={() => handleSort("status")}
              >
                {ae.status} {sortArrow("status")}
              </TableHead>
              <TableHead className="text-right">{ae.actions}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {invoices.map((invoice) => (
              <TableRow key={invoice.invoice.id} className="transition-colors">
                <TableCell className="font-medium">
                  {invoice.invoice.invoice_number}
                </TableCell>
                {!isExtraSmall && (
                  <TableCell>{`${invoice.customer.firstName} ${invoice.customer.lastName}`}</TableCell>
                )}
                <TableCell>{`${invoice.payment.amount.toLocaleString()} kr`}</TableCell>
                {!isMobile && (
                  <TableCell>
                    {formatDate(invoice.invoice.created_at)}
                  </TableCell>
                )}
                {!isMobile && (
                  <TableCell>{formatDate(invoice.payment.due_date)}</TableCell>
                )}
                <TableCell>{invoice.listing.regNumber}</TableCell>
                <TableCell>
                  <Badge
                    variant={
                      String(invoice.payment.status) === "paid"
                        ? "default"
                        : String(invoice.payment.status) === "unpaid"
                          ? "secondary"
                          : invoice.payment.due_date &&
                              String(invoice.payment.status) !== "paid" &&
                              !isNaN(
                                new Date(invoice.payment.due_date).getTime(),
                              ) &&
                              new Date(invoice.payment.due_date) < new Date()
                            ? "destructive"
                            : String(invoice.payment.status) === "pending"
                              ? "secondary"
                              : "destructive"
                    }
                  >
                    {String(invoice.payment.status) === "paid"
                      ? o.filter.paid
                      : String(invoice.payment.status) === "unpaid"
                        ? o.filter.unpaid
                        : invoice.payment.due_date &&
                            String(invoice.payment.status) !== "paid" &&
                            !isNaN(
                              new Date(invoice.payment.due_date).getTime(),
                            ) &&
                            new Date(invoice.payment.due_date) < new Date()
                          ? o.filter.overdue
                          : String(invoice.payment.status) === "pending"
                            ? o.filter.pending
                            : o.filter.overdue}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <div
                    className="flex gap-1 justify-end"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        setSelectedInvoice(invoice);
                        setIsInvoicePopupOpen(true);
                      }}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    {!isExtraSmall && (
                      <>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={async () => {
                            if (invoice.invoice.invoice_file) {
                              try {
                                // If mediaId exists, fetch presigned URL on-demand via hook
                                let downloadUrl =
                                  invoice.invoice.invoice_file.url;
                                if (
                                  invoice.invoice.invoice_file.mediaId &&
                                  !invoice.invoice.invoice_file.url
                                ) {
                                  downloadUrl = await getDownloadUrlAsync({
                                    mediaId:
                                      invoice.invoice.invoice_file.mediaId,
                                    expiresIn: 3600,
                                  });
                                }
                                const filename = await downloadFile(
                                  downloadUrl,
                                  invoice.invoice.invoice_file.name,
                                );
                                toast.success(o.invoicePopup.downloadStarted, {
                                  description: o.invoicePopup.downloadDescription.replace(
                                      "{filename}",
                                      filename,
                                    ),
                                });
                              } catch (error) {
                                const errorMessage =
                                  error instanceof Error
                                    ? error.message
                                    : o.invoicePopup.unknownError;
                                toast.error(o.invoicePopup.downloadError, { description: `${invoice.invoice.invoice_file.name}\n${o.invoicePopup.errorLabel}: ${errorMessage}` });
                              }
                            }
                          }}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={async () => {
                            try {
                              await InvoiceService.sendInvoice(
                                invoice.invoice.id,
                              );
                              toast.success(o.toasts.invoiceSent, { description: o.toasts.invoiceSentDescription });
                            } catch (error) {
                              toast.error(o.toasts.invoiceSendError, {
                                description: error instanceof Error
                                    ? error.message
                                    : o.invoicePopup.unknownError,
                              });
                            }
                          }}
                        >
                          <Send className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Invoice Popup */}
      {selectedInvoice && (
        <InvoicePopup
          invoice={selectedInvoice}
          onClose={() => {
            setIsInvoicePopupOpen(false);
            setSelectedInvoice(null);
          }}
        />
      )}
    </>
  );
};
